import { useRef, useState } from 'react';
import { Tab, Tabs } from '@op/opds.opux';
import { space } from '@op/opds.tokens';
import { IconInformation } from '@op/opds.opux-icons/indicator';
import { Message, useMessage, useMessageGetter } from '@messageformat/react';
import Selections from './Selections';
import ResultTile from './ResultTile';
import { useIsMobile } from '../../../util/mediaQuery';
import ReturnToTop from '../../ReturnToTop';

interface InnerOptionItem {
  name: JSX.Element;
  share: number;
}

interface OptionItem {
  id: number;
  name: JSX.Element;
  description?: JSX.Element;
  icon?: JSX.Element;
  instruments: InnerOptionItem[];
  locations: InnerOptionItem[];
  fields: InnerOptionItem[];
}

interface SelectedItem extends OptionItem {
  investment: number;
}

interface ShareItem {
  label: string;
  value: number;
}

interface DiversityAnalyzerProps {
  options: OptionItem[];
  type: 'STOCK' | 'FUND';
}

const getShares = (
  selected: SelectedItem[],
  key: 'instruments' | 'locations' | 'fields',
  getMessage: (id: string) => string,
): ShareItem[] => {
  // Calculate all shares based on shares and investment
  const allSharesWithThisKey: ShareItem[] = [];
  selected.forEach((selectedItem: SelectedItem) => {
    const arrayInThisObject: InnerOptionItem[] = [...selectedItem[key]];
    // Browse copied array and store name and share of sum
    arrayInThisObject.forEach((element: InnerOptionItem) =>
      allSharesWithThisKey.push({
        label: getMessage(element.name.props.id),
        value: (element.share / 100) * selectedItem.investment,
      }),
    );
  });

  // Get unique labels
  const uniqueLabels: string[] = [];
  allSharesWithThisKey.forEach(
    (element: ShareItem) =>
      !uniqueLabels.includes(element.label) && uniqueLabels.push(element.label),
  );

  // Calculate sum of shares per each unique
  const shares: ShareItem[] = [];
  uniqueLabels.forEach((uniqueLabel: string) => {
    const items = allSharesWithThisKey.filter((element) => element.label === uniqueLabel);
    const sum = items.reduce((accumulated, current) => accumulated + current.value, 0);
    shares.push({ label: uniqueLabel, value: sum });
  });

  return shares;
};

const getScoreMax = (
  options: OptionItem[],
  key: 'instruments' | 'locations' | 'fields',
  getMessage: (id: string) => string,
): number => {
  // Get all names with this key
  const allNamesWithThisKey: string[] = [];
  options.forEach((element: OptionItem) => {
    const arrayInThisObject: InnerOptionItem[] = [...element[key]];
    // Browse copied array and store name and share of sum
    arrayInThisObject.forEach((innerElement: InnerOptionItem) => {
      allNamesWithThisKey.push(getMessage(innerElement.name.props.id));
    });
  });
  // Get names that are unique
  const uniqueNames: string[] = [];
  allNamesWithThisKey.forEach(
    (element: string) => !uniqueNames.includes(element) && uniqueNames.push(element),
  );

  return uniqueNames.length;
};

const DiversityAnalyzer = ({ options, type }: DiversityAnalyzerProps): JSX.Element => {
  const isMobile = useIsMobile();
  const [selected, setSelected] = useState<SelectedItem[]>([]);

  const getMessage = useMessageGetter('');

  const resultTile1Title = useMessage('course7.retultTile.tile1.label');
  const resultTile2Title = useMessage('course7.retultTile.tile2.label');
  const resultTile3Title = useMessage('course7.retultTile.tile3.label');

  const resultTile1TabTitle = useMessage('course7.retultTile.tile1.tab_label');
  const resultTile2TabTitle = useMessage('course7.retultTile.tile2.tab_label');
  const resultTile3TabTitle = useMessage('course7.retultTile.tile3.tab_label');

  const tab1Label = useMessage('course7.task2.tab1.label');
  const tab2Label = useMessage('course7.task2.tab2.label');

  const halfIndex = Math.ceil(options.length / 2);
  const optionsFirstHalf = options.slice(0, halfIndex);
  const optionsSecondHalf = options.slice(halfIndex);
  const targetRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<{ [key: string]: number }>({
    second_div: 5,
  });

  const handleTabChange = (divId: string, index: number): void => {
    if (divId === 'second_div') {
      setActiveTab((prev) => ({
        ...prev,
        second_div: index + 5,
      }));
    }
  };

  return (
    <>
      {isMobile && (
        <p className="opds-styles-v1-text-title-large" style={{ textAlign: 'center' }}>
          <Message id="course7.task2.question1.title" />
        </p>
      )}
      {type === 'STOCK' ? (
        <div ref={targetRef} id="second_div">
          <Tabs
            small
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
            active={activeTab.second_div}
            onChange={(event, index) => handleTabChange('second_div', index)}
          >
            <Tab label={tab1Label} id="sixth_tab">
              <Selections
                selected={selected}
                setSelected={setSelected}
                options={optionsFirstHalf}
                type={type}
              />
            </Tab>
            <Tab label={tab2Label} id="seventh_tab">
              <Selections
                selected={selected}
                setSelected={setSelected}
                options={optionsSecondHalf}
                type={type}
              />
            </Tab>
          </Tabs>
        </div>
      ) : (
        <div ref={targetRef}>
          <Selections
            selected={selected}
            setSelected={setSelected}
            options={options}
            type={type}
          />
        </div>
      )}
      {isMobile && (
        <p
          className="opds-styles-v1-text-title-large"
          style={{ textAlign: 'center', marginTop: space.default }}
        >
          <Message id="course7.task2.question2.title" />
        </p>
      )}
      {isMobile && type === 'FUND' ? (
        <Tabs
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Tab label={resultTile3TabTitle}>
            <ResultTile
              title={resultTile3Title}
              shares={getShares(selected, 'instruments', getMessage)}
              scoreMax={getScoreMax(options, 'instruments', getMessage)}
              index={0}
            />
          </Tab>
          <Tab label={resultTile1TabTitle}>
            <ResultTile
              title={resultTile1Title}
              shares={getShares(selected, 'fields', getMessage)}
              scoreMax={getScoreMax(options, 'fields', getMessage)}
              index={1}
            />
          </Tab>
          <Tab label={resultTile2TabTitle}>
            <ResultTile
              title={resultTile2Title}
              shares={getShares(selected, 'locations', getMessage)}
              scoreMax={getScoreMax(options, 'locations', getMessage)}
              index={2}
            />
          </Tab>
        </Tabs>
      ) : (
        <>
          {type === 'FUND' && (
            <ResultTile
              title={resultTile3Title}
              shares={getShares(selected, 'instruments', getMessage)}
              scoreMax={getScoreMax(options, 'instruments', getMessage)}
              index={0}
            />
          )}
          <ResultTile
            title={resultTile1Title}
            shares={getShares(selected, 'fields', getMessage)}
            scoreMax={getScoreMax(options, 'fields', getMessage)}
            index={1}
          />
          <ResultTile
            title={resultTile2Title}
            shares={getShares(selected, 'locations', getMessage)}
            scoreMax={getScoreMax(options, 'locations', getMessage)}
            index={2}
          />
        </>
      )}
      <p
        className="opds-styles-v1-text-label-small"
        style={{ display: 'flex', paddingBottom: space.default, textAlign: 'left' }}
      >
        <IconInformation />
        {type === 'STOCK' ? (
          <Message id="course7.task2.sources" />
        ) : (
          <Message id="course7.task3.sources" />
        )}
      </p>
      {type === 'STOCK' && (
        <ReturnToTop
          msgId="return_to_tabs"
          targetRef={targetRef}
          index={activeTab.second_div}
        />
      )}
    </>
  );
};

export default DiversityAnalyzer;
